import { useState } from 'react';
import { CalendarPlus, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Aviso } from '@/components/ui/aviso';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { adminService } from '../services/admin-service';
import type { VencimientoDeLaPrueba } from '../types/admin.types';
import { TEXTOS_DEL_PLAZO as T, textoDelPlazo } from '../lib/demo-textos';

const fecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' });

/** Las opciones rápidas de «Extender prueba»; el campo acepta cualquier número de 1 a 90. */
const DIAS_SUGERIDOS = [7, 15, 30];

/**
 * Demo que vence (C, 2-nov): el plazo de la prueba en la ficha de una empresa en prueba —cuándo vence,
 * cuánto falta, si ya venció— y «Extender prueba». No deduce nada: lee `vencimiento` del servidor.
 */
export function PlazoDeLaPrueba({
  empresaId,
  nombre,
  vencimiento,
  archivada,
  onExtendida,
}: {
  empresaId: string;
  nombre: string;
  vencimiento: VencimientoDeLaPrueba;
  archivada: boolean;
  onExtendida: () => void;
}) {
  const { toast } = useToast();
  const [abierto, setAbierto] = useState(false);
  const [dias, setDias] = useState('15');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = Number(dias);
  const valido = Number.isInteger(n) && n >= 1 && n <= 90;
  const resumen = textoDelPlazo(vencimiento);

  const extender = async () => {
    setTrabajando(true);
    setError(null);
    try {
      const r = await adminService.extenderPrueba(empresaId, n);
      toast({
        title: T.hecho(nombre, fecha(r.venceEl)),
        description: r.reactivada ? T.reactivada : undefined,
      });
      setAbierto(false);
      onExtendida();
    } catch (err) {
      setError(mensajeDeError(err, T.noSePudo));
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" aria-label="Plazo de la prueba">
      <span className="font-medium">{T.titulo}:</span>
      {vencimiento.venceEl ? (
        <span className={vencimiento.estado === 'VENCIDA' ? 'text-destructive' : undefined}>
          {vencimiento.estado === 'VENCIDA'
            ? T.vencio(fecha(vencimiento.venceEl))
            : T.vence(fecha(vencimiento.venceEl))}
        </span>
      ) : (
        <span className="text-muted-foreground">{T.sinFecha}</span>
      )}
      {resumen && vencimiento.estado !== 'VENCIDA' && (
        <Badge
          variant={vencimiento.estado === 'POR_VENCER' ? 'warning' : 'secondary'}
          className="px-2 py-0 text-[11px]"
        >
          {resumen}
        </Badge>
      )}
      {/* Una archivada no se toca desde acá (409): primero se restaura. Sin prueba, el plan se cambia con «Asignar plan». */}
      {!archivada && vencimiento.esPrueba && (
        <Button variant="outline" size="sm" onClick={() => setAbierto(true)}>
          <CalendarPlus className="mr-2 h-4 w-4" /> {T.boton}
        </Button>
      )}

      <Dialog open={abierto} onOpenChange={(v) => !trabajando && setAbierto(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{T.dialogoTitulo(nombre)}</DialogTitle>
            <DialogDescription>{T.dialogoAyuda}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex flex-wrap gap-2">
              {DIAS_SUGERIDOS.map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={n === d ? 'default' : 'outline'}
                  onClick={() => setDias(String(d))}
                >
                  {d} días
                </Button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm">
              {T.dias}
              <Input
                type="number"
                min={1}
                max={90}
                step={1}
                className="h-8 w-24"
                value={dias}
                onChange={(e) => setDias(e.target.value)}
                aria-label={T.dias}
              />
            </label>
            {error && <Aviso tono="alerta">{error}</Aviso>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)} disabled={trabajando}>
              {T.cancelar}
            </Button>
            <Button onClick={extender} disabled={trabajando || !valido}>
              {trabajando ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {T.trabajando}
                </>
              ) : (
                T.confirmar
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
